import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { api } from 'api';
import { useStore } from 'models/Provider';

import { useIsHalloweenSignin } from './halloween/halloween-season';
import { Signin } from './sign-in';
import { SigninSMS } from './SigninSMS';
import { SigninTwoFactor } from './SigninTwoFactor';
import { useSigninForm } from './use-signin-form';

function SigninPage({ setTwoFactor }) {
  const { t } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isHalloween = useIsHalloweenSignin();

  const [state, setState] = useState({
    uuid: '',
    code: '',
    needsTwoFactor: false,
    needsSMS: false,
    sendingSMS: false,
    error: '',
    disable: false,
  });
  const locationState = location.state;
  const client = useQueryClient();

  const signinForm = useSigninForm({
    shouldCheer: isHalloween,
    onTwoFactorChallenge: ({ needs2FA, uuid }) => {
      setTwoFactor(needs2FA);
      setState((old) => ({ ...old, needsTwoFactor: true, uuid }));
    },
  });

  function handleChange(name, value) {
    setState((old) => ({ ...old, [name]: value, error: '' }));
  }

  async function signInTwoFactor() {
    setState((old) => ({ ...old, error: '', disable: true }));
    const { uuid, code } = state;

    const res = await api.users.signIn2fa(uuid, code);
    if (res.error) {
      setState((old) => ({ ...old, error: res.data.code, disable: false }));
    } else {
      client.resetQueries();
      const redir = locationState && (locationState.from ? locationState.from : '/');
      redir && navigate(redir);
    }
  }

  async function signInSMS() {
    setState((old) => ({ ...old, error: '', disable: true }));
    const { uuid, code } = state;

    const res = await api.users.signInSMS(uuid, code);
    if (res.error) {
      setState((old) => ({ ...old, error: res.data.code, disable: false }));
    } else {
      client.resetQueries();
      const redir = locationState && (locationState.from ? locationState.from : '/');
      redir && navigate(redir);
    }
  }

  async function useSms() {
    setState((old) => ({ ...old, error: '', sendingSMS: true }));
    const { uuid, code } = state;

    const res = await api.users.signInSendBackupSMS(uuid, code);
    if (res.error) {
      setState((old) => ({ ...old, sendingSMS: false, error: res.data.code, disable: false }));
    } else {
      setState((old) => ({ ...old, needsSMS: true, needsTwoFactor: false, disable: false }));
    }
  }

  if (state.needsSMS) {
    return (
      <SigninSMS
        t={t}
        code={state.code}
        onChange={handleChange}
        onSubmit={signInSMS}
        error={state.error}
        disableSubmit={state.disable}
      />
    );
  }

  if (state.needsTwoFactor) {
    return (
      <SigninTwoFactor
        t={t}
        code={state.code}
        onChange={handleChange}
        onSubmit={signInTwoFactor}
        useSms={useSms}
        sendingSMS={state.sendingSMS}
        error={state.error}
        disableSubmit={state.disable}
      />
    );
  }

  return <Signin form={signinForm.form} scene={signinForm.scene} isHalloween={isHalloween} />;
}

export default observer(SigninPage);
